import React, { useState, useEffect } from 'react';
import { Box, IconButton } from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import InventoryIcon from '@mui/icons-material/Inventory';
import { ImagenCatalogo } from '../types';

interface CatalogoImageCarouselProps {
  imagenes: ImagenCatalogo[];
  height?: number;
  categoriaColor?: string;
  onCurrentImageChange?: (url: string | null) => void;
}

type Direction = 'left' | 'right';

export function CatalogoImageCarousel({
  imagenes,
  height = 180,
  categoriaColor = '#e8d8e8',
  onCurrentImageChange,
}: CatalogoImageCarouselProps) {
  // Ordenar por .orden ASC
  const sorted = [...imagenes].sort((a, b) => a.orden - b.orden);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState<Direction>('left');
  const [animKey, setAnimKey] = useState(0); // fuerza re-render de la animación

  const hasMultiple = sorted.length > 1;
  const currentImage = sorted[currentIndex]?.url;

  useEffect(() => {
    onCurrentImageChange?.(currentImage ?? null);
  }, [currentIndex, currentImage, onCurrentImageChange]);

  const goTo = (index: number, dir: Direction) => {
    setDirection(dir);
    setAnimKey((k) => k + 1);
    setCurrentIndex(index);
  };

  const handlePrev = (e: React.MouseEvent) => {
    e.stopPropagation();
    const prev = currentIndex === 0 ? sorted.length - 1 : currentIndex - 1;
    goTo(prev, 'right');
  };

  const handleNext = (e: React.MouseEvent) => {
    e.stopPropagation();
    const next = currentIndex === sorted.length - 1 ? 0 : currentIndex + 1;
    goTo(next, 'left');
  };

  const handleThumbnailClick = (e: React.MouseEvent, index: number) => {
    e.stopPropagation();
    if (index === currentIndex) return;
    goTo(index, index > currentIndex ? 'left' : 'right');
  };

  // Sin imágenes → placeholder
  if (sorted.length === 0) {
    return (
      <Box
        sx={{
          height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: categoriaColor,
          flexShrink: 0,
        }}
      >
        <InventoryIcon sx={{ fontSize: 64, color: '#b39ab3' }} />
      </Box>
    );
  }

  // 1 imagen → estática, sin controles
  if (sorted.length === 1) {
    return (
      <Box
        data-testid="carousel-imagen-principal"
        sx={{
          height,
          overflow: 'hidden',
          flexShrink: 0,
          backgroundColor: '#fff',
        }}
      >
        <img
          src={sorted[0].url}
          alt="Imagen del producto"
          style={{ width: '100%', height: '100%', objectFit: 'contain' }}
          onError={(e) => {
            (e.target as HTMLImageElement).style.display = 'none';
          }}
        />
      </Box>
    );
  }

  // Múltiples imágenes → carrusel completo
  const animationName = direction === 'left' ? 'carouselSlideInLeft' : 'carouselSlideInRight';

  return (
    <Box
      sx={{
        flexShrink: 0,
        // Mostrar flechas en hover de este contenedor
        '&:hover .carousel-arrow': {
          opacity: 1,
        },
      }}
    >
      {/* Zona imagen principal */}
      <Box
        sx={{
          position: 'relative',
          height,
          overflow: 'hidden',
          backgroundColor: '#fff',
        }}
      >
        <img
          key={animKey}
          data-testid="carousel-imagen-principal"
          src={currentImage}
          alt={`Imagen ${currentIndex + 1}`}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            animation: `${animationName} 250ms ease-out`,
          }}
          onError={(e) => {
            (e.target as HTMLImageElement).style.display = 'none';
          }}
        />

        {/* Flecha anterior */}
        <IconButton
          className="carousel-arrow"
          data-testid="carousel-btn-prev"
          onClick={handlePrev}
          aria-label="Imagen anterior"
          size="small"
          sx={{
            position: 'absolute',
            left: 4,
            top: '50%',
            transform: 'translateY(-50%)',
            width: 28,
            height: 28,
            backgroundColor: 'rgba(0,0,0,0.45)',
            color: '#fff',
            opacity: 0,
            transition: 'opacity 0.2s ease',
            zIndex: 2,
            '&:hover': { backgroundColor: 'rgba(0,0,0,0.75)' },
          }}
        >
          <ArrowBackIosNewIcon sx={{ fontSize: 14 }} />
        </IconButton>

        {/* Flecha siguiente */}
        <IconButton
          className="carousel-arrow"
          data-testid="carousel-btn-next"
          onClick={handleNext}
          aria-label="Siguiente imagen"
          size="small"
          sx={{
            position: 'absolute',
            right: 4,
            top: '50%',
            transform: 'translateY(-50%)',
            width: 28,
            height: 28,
            backgroundColor: 'rgba(0,0,0,0.45)',
            color: '#fff',
            opacity: 0,
            transition: 'opacity 0.2s ease',
            zIndex: 2,
            '&:hover': { backgroundColor: 'rgba(0,0,0,0.75)' },
          }}
        >
          <ArrowForwardIosIcon sx={{ fontSize: 14 }} />
        </IconButton>
      </Box>

      {/* Custom Paging — strip de miniaturas */}
      <Box
        data-testid="carousel-strip"
        sx={{
          display: 'flex',
          gap: '4px',
          px: '4px',
          py: '4px',
          backgroundColor: 'rgba(0,0,0,0.04)',
          overflowX: 'auto',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
        {sorted.map((img, index) => (
          <Box
            key={index}
            component="button"
            data-testid={`carousel-miniatura-${index}`}
            onClick={(e: React.MouseEvent) => handleThumbnailClick(e, index)}
            aria-label={`Ir a imagen ${index + 1}`}
            sx={{
              flexShrink: 0,
              width: 40,
              height: 40,
              borderRadius: '4px',
              overflow: 'hidden',
              cursor: 'pointer',
              padding: 0,
              border: index === currentIndex
                ? '2px solid #3d1e3d'
                : '2px solid transparent',
              transition: 'border-color 0.15s ease',
              backgroundColor: '#fff',
              '&:hover': {
                border: '2px solid rgba(61,30,61,0.5)',
              },
            }}
          >
            <img
              src={img.url}
              alt={`Miniatura ${index + 1}`}
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
          </Box>
        ))}
      </Box>

      {/* CSS keyframes */}
      <style>{`
        @keyframes carouselSlideInLeft {
          from { transform: translateX(100%); opacity: 0.8; }
          to   { transform: translateX(0);    opacity: 1;   }
        }
        @keyframes carouselSlideInRight {
          from { transform: translateX(-100%); opacity: 0.8; }
          to   { transform: translateX(0);     opacity: 1;   }
        }
      `}</style>
    </Box>
  );
}
